import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/api/api-client';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Phone, Building, User, Folder, Play } from 'lucide-react';
import { motion } from 'framer-motion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { currentLocale } from '@/i18n/locale';
import { VFStatusTag } from '@/components/vf/StatusTag';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';

interface ClientLite {
  id?: string;
  email?: string | null;
}

// ADR-032: canonical 'active' | 'do_not_call' | 'placeholder'; the legacy
// values (pending, calling, contacted, completed, skipped) read as 'active'.
type ProspectStatus = 'active' | 'do_not_call' | 'placeholder' | string;

interface Prospect {
  id: string;
  prospect_name?: string | null;
  prospect_phone?: string | null;
  prospect_email?: string | null;
  prospect_company?: string | null;
  prospect_title?: string | null;
  notes?: string | null;
  status?: ProspectStatus;
  source?: string | null;
  created_date?: string | Date | null;
}

interface CallRow {
  id?: string;
  prospect_phone?: string | null;
  call_status?: string | null;
  call_outcome?: string | null;
  recording_url?: string | null;
  created_date?: string | Date | null;
}

interface ProspectQueueApi {
  filter(query: Record<string, unknown>, sort?: string): Promise<Prospect[]>;
}

interface ParallelCallApi {
  filter(query: Record<string, unknown>): Promise<CallRow[]>;
}

interface ApiShape {
  entities: {
    ProspectQueue: ProspectQueueApi;
    ParallelCall: ParallelCallApi;
  };
}

/**
 * QA-W5-2: the queue status as the one status map knows it. A list's
 * placeholder row (an empty list) is not a prospect: never shown.
 */
export function queueStatusKey(status: ProspectStatus | null | undefined): 'active' | 'do_not_call' | 'placeholder' {
  if (status === 'do_not_call') return 'do_not_call';
  if (status === 'placeholder') return 'placeholder';
  return 'active';
}

/** QA-W5-2: what the last call says: its outcome when qualified, else its telephony status. */
export function lastCallStatus(call: CallRow | undefined): string | null {
  return call ? call.call_outcome || call.call_status || null : null;
}

interface ProspectsSectionProps {
  client?: ClientLite | null;
}

export default function ProspectsSection({ client }: ProspectsSectionProps) {
  const { t } = useTranslation(['admin', 'dialer']);
  const [openPlayerId, setOpenPlayerId] = useState<string | null>(null);
  const { data: queueRows = [], isLoading } = useQuery<Prospect[]>({
    queryKey: ['prospect-queue', client?.email],
    queryFn: () =>
      (api as unknown as ApiShape).entities.ProspectQueue.filter(
        {},
        '-created_date',
      ),
    enabled: !!client?.email,
  });

  const { data: allCalls = [] } = useQuery<CallRow[]>({
    queryKey: ['all-calls', client?.email],
    queryFn: () =>
      (api as unknown as ApiShape).entities.ParallelCall.filter({}),
    enabled: !!client?.email,
  });

  const prospects = queueRows.filter((p) => queueStatusKey(p.status) !== 'placeholder');
  // ADR-032: every prospect still in the queue ('active' and the legacy values).
  const pendingCount = prospects.filter((p) => queueStatusKey(p.status) === 'active').length;

  const prospectsBySource = prospects.reduce<Record<string, Prospect[]>>((acc, prospect) => {
    const source = prospect.source || 'Revcall Client Dashboard';
    const bucket = acc[source] ?? [];
    bucket.push(prospect);
    acc[source] = bucket;
    return acc;
  }, {});

  const sources = Object.keys(prospectsBySource);

  const renderProspectsTable = (prospectsToShow: Prospect[]) => (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="relative overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">{t('admin:backoffice.prospects.columns.name')}</th>
              <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">{t('admin:backoffice.prospects.columns.title')}</th>
              <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">{t('admin:backoffice.prospects.columns.company')}</th>
              <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">{t('admin:backoffice.prospects.columns.phone')}</th>
              <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">{t('admin:backoffice.prospects.columns.calls')}</th>
              <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">{t('admin:backoffice.prospects.columns.lastCall')}</th>
              <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">{t('admin:backoffice.prospects.columns.callStatus')}</th>
              <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">{t('admin:backoffice.prospects.columns.queueStatus')}</th>
              <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">{t('admin:backoffice.prospects.columns.recording')}</th>
            </tr>
          </thead>
          <tbody>
            {prospectsToShow.map((prospect, index) => {
              const prospectCalls = allCalls.filter((c) => c.prospect_phone === prospect.prospect_phone);
              const lastCall = prospectCalls.slice().sort(
                (a, b) =>
                  new Date(b.created_date || 0).getTime() - new Date(a.created_date || 0).getTime(),
              )[0];
              const callCount = prospectCalls.length;
              const queueKey = queueStatusKey(prospect.status);
              const callStatus = lastCallStatus(lastCall);

              return (
                <motion.tr
                  key={prospect.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-sm font-medium text-gray-900">{prospect.prospect_name}</p>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <span className="text-xs text-gray-600">{prospect.prospect_title || '-'}</span>
                  </td>
                  <td className="px-4 py-2">
                    {prospect.prospect_company ? (
                      <div className="flex items-center gap-2">
                        <Building className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-700">{prospect.prospect_company}</span>
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700 font-mono text-xs">{prospect.prospect_phone}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <span className="text-gray-900 font-semibold">{callCount}</span>
                  </td>
                  <td className="px-4 py-2">
                    {lastCall ? (
                      <span className="text-xs text-gray-600">
                        {new Date(lastCall.created_date || 0).toLocaleDateString(currentLocale())}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">{t('admin:backoffice.prospects.never')}</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {callStatus ? (
                      <VFStatusTag status={callStatus} label={outcomeLabel(callStatus, t)} />
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {queueKey === 'do_not_call' ? (
                      <VFStatusTag status="do_not_call" label={outcomeLabel('do_not_call', t)} />
                    ) : (
                      <VFStatusTag status="pending" label={t('admin:backoffice.prospects.status.active')} />
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {lastCall?.recording_url ? (
                      <div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenPlayerId(openPlayerId === prospect.id ? null : prospect.id);
                          }}
                        >
                          <Play className="w-4 h-4 mr-1" /> {t('admin:backoffice.prospects.play')}
                        </Button>
                        {openPlayerId === prospect.id && (
                          <audio
                            src={lastCall.recording_url}
                            controls
                            controlsList="nodownload noplaybackrate"
                            className="mt-2 w-56 h-8"
                          />
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('admin:backoffice.prospects.title')}</h2>
          <p className="text-gray-500 mt-1">
            {pendingCount} {t('common:entity.prospect', { count: pendingCount })}{' '}
            {t('admin:backoffice.prospects.pendingCountSuffix')}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <p className="text-gray-500">{t('admin:backoffice.prospects.loading')}</p>
        </div>
      ) : prospects.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Phone className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg mb-2">{t('admin:backoffice.prospects.emptyTitle')}</p>
          <p className="text-gray-400 text-sm">{t('admin:backoffice.prospects.emptyHint')}</p>
        </div>
      ) : sources.length === 1 ? (
        renderProspectsTable(prospects)
      ) : (
        <Tabs defaultValue={sources[0]} className="w-full">
          <TabsList className="relative max-w-full justify-start overflow-x-auto bg-white border border-gray-200 p-1">
            {sources.map((source) => (
              <TabsTrigger key={source} value={source} className="flex items-center gap-2">
                <Folder className="w-4 h-4" />
                {source}
                <Badge variant="secondary" className="ml-1">
                  {prospectsBySource[source]?.length ?? 0}
                </Badge>
              </TabsTrigger>
            ))}
          </TabsList>
          {sources.map((source) => (
            <TabsContent key={source} value={source} className="mt-4">
              {renderProspectsTable(prospectsBySource[source] ?? [])}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}
